import { CheckSquare, Plus, Trash2 } from "lucide-react";
import { ReportCard } from "./ReportCard";

interface KeyQuestionsProps {
  questions: string[];
  isEditing?: boolean;
  onUpdate?: (questions: string[]) => void;
}

export function KeyQuestions({ questions, isEditing, onUpdate }: KeyQuestionsProps) {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <div className="mb-6">
        <h2 className="text-3xl font-sans font-semibold text-[#1e3a5f] mb-2">Key Questions for Your Decision</h2>
        <div className="h-1 w-20 bg-[#d4975c] rounded-full" />
        <p className="text-[#5a6a7a] mt-3">
          Consider these strategic questions based on your organizational context and priorities.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {questions.map((question, index) => (
          <ReportCard key={index} className="p-6 border border-[#e8e3dc] hover:shadow-lg transition-all hover:border-[#d4975c]">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-[#d4975c] text-white flex items-center justify-center font-bold text-lg shadow-md">
                  {index + 1}
                </div>
              </div>
              <div className="flex-1">
                {isEditing ? (
                  <div className="flex gap-2">
                    <textarea
                      className="flex-1 text-[#3a4a5a] leading-relaxed bg-white border border-slate-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-[#d4975c]/50 resize-y"
                      value={question}
                      onChange={(e) => {
                        const updated = [...questions];
                        updated[index] = e.target.value;
                        onUpdate?.(updated);
                      }}
                      rows={2}
                    />
                    <button
                      onClick={() => {
                        const updated = questions.filter((_, i) => i !== index);
                        onUpdate?.(updated);
                      }}
                      className="p-1.5 rounded-md hover:bg-rose-50 text-rose-500 self-start"
                      title="Remove question"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <p className="text-[#3a4a5a] leading-relaxed">{question}</p>
                )}
              </div>
            </div>
          </ReportCard>
        ))}
      </div>

      {isEditing && (
        <button
          onClick={() => onUpdate?.([...questions, ""])}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#d4975c] border border-[#d4975c] rounded-lg hover:bg-[#d4975c]/5 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Question
        </button>
      )}

      <ReportCard className="mt-8 p-4 sm:p-6 lg:p-8 bg-[#f0ebe4] border border-[#e8e3dc]">
        <div>
          <h3 className="text-xl font-sans font-semibold text-[#1e3a5f] mb-2">Next Steps</h3>
          <p className="text-[#5a6a7a] mb-4 leading-relaxed">
            This evaluation presents trade-offs for stakeholder consideration. The appropriate decision 
            depends on your organization's priorities, risk tolerance, and strategic AI objectives.
          </p>
          <div className="flex flex-wrap gap-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-[#e8e3dc] text-sm">
              <CheckSquare className="w-4 h-4 text-[#d4975c]" />
              <span className="text-[#3a4a5a]">Schedule vendor clarification call</span>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-[#e8e3dc] text-sm">
              <CheckSquare className="w-4 h-4 text-[#d4975c]" />
              <span className="text-[#3a4a5a]">Assess against organizational priorities</span>
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-[#e8e3dc] text-sm">
              <CheckSquare className="w-4 h-4 text-[#d4975c]" />
              <span className="text-[#3a4a5a]">Review with technical stakeholders</span>
            </div>
          </div>
        </div>
      </ReportCard>
    </div>
  );
}
